import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { CartDiscountUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  // Preserve eligible shipping codes too: Liquid allocations only list price discounts.
  static ITG_knownCodes = new Map();

  #ITG_setBusy(ITG_busy) {
    this.toggleAttribute('aria-busy', ITG_busy);
    this.querySelectorAll('button, input').forEach((ITG_control) => {
      ITG_control.disabled = ITG_busy;
    });
    // Prevent proceeding before Shopify has confirmed this discount change.
    const ITG_checkout = this.closest('.cart-page, theme-drawer')?.querySelector('[name="checkout"]');
    if (ITG_checkout) {
      if (ITG_busy) {
        ITG_checkout.dataset.itgWasDisabled = String(ITG_checkout.disabled);
        ITG_checkout.disabled = true;
      } else if (ITG_checkout.dataset.itgWasDisabled === 'false') {
        ITG_checkout.disabled = false;
        delete ITG_checkout.dataset.itgWasDisabled;
      }
    }
  }

  #ITG_status(ITG_message) {
    const ITG_section = document.getElementById(`shopify-section-${this.dataset.sectionId}`);
    const ITG_status = ITG_section?.querySelector('.ITG-cart-review__coupon-status') || this.querySelector('.ITG-cart-review__coupon-status');
    if (ITG_status) ITG_status.textContent = ITG_message;
  }

  #ITG_requestError() {
    this.#ITG_status('Could not update the coupon. Check your connection and try again.');
  }


  #ITG_syncPills(ITG_data) {
    const ITG_scope = document.getElementById(`shopify-section-${this.dataset.sectionId}`);
    const ITG_component = ITG_scope?.querySelector('cart-discount-component') || this;
    const ITG_list = ITG_component.querySelector('.cart-discount__codes');
    if (!ITG_list) return;
    const ITG_present = [...ITG_list.querySelectorAll('[data-discount-code]')].map((ITG_pill) => ITG_pill.dataset.discountCode.toLowerCase());
    for (const ITG_entry of ITG_data.discount_codes.filter((ITG_code) => ITG_code.applicable)) {
      if (ITG_present.includes(ITG_entry.code.toLowerCase())) continue;
      const ITG_pill = document.createElement('li');
      ITG_pill.className = 'cart-discount__pill';
      ITG_pill.dataset.discountCode = ITG_entry.code;
      const ITG_label = document.createElement('p');
      ITG_label.className = 'cart-discount__pill-code';
      ITG_label.textContent = ITG_entry.code;
      const ITG_remove = document.createElement('button');
      ITG_remove.type = 'button';
      ITG_remove.className = 'cart-discount__pill-remove button-unstyled';
      ITG_remove.setAttribute('on:click', '/removeDiscount');
      ITG_remove.setAttribute('aria-label', `Remove coupon ${ITG_entry.code}`);
      ITG_remove.textContent = '�';
      ITG_pill.append(ITG_label, ITG_remove);
      ITG_list.append(ITG_pill);
    }
  }

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value.trim();
    if (!discountCodeValue || this.#activeFetch) return;
    discountCode.value = discountCodeValue;

    const existingDiscounts = this.#existingDiscounts();
    if (existingDiscounts.some((ITG_code) => ITG_code.toLowerCase() === discountCodeValue.toLowerCase())) {
      this.#ITG_status('This coupon is already applied.');
      return;
    }
    const abortController = this.#createAbortController();
    this.#ITG_setBusy(true);
    this.#ITG_status('Checking coupon…');

    cartDiscountError.classList.add('hidden');
    cartDiscountErrorDiscountCode.classList.add('hidden');
    cartDiscountErrorShipping.classList.add('hidden');

    const allDiscountCodes = [...existingDiscounts, discountCodeValue];
    const deferredPromise = CartDiscountUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartDiscountUpdateEvent({
        discountCodes: allDiscountCodes.map((code) => ({ code })),
        promise: deferredPromise.promise,
      })
    );

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: allDiscountCodes.join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(`${Theme.routes.cart_update_url.replace(/\.js$/, '')}.js`, {
        ...config,
        signal: abortController.signal,
      });

      if (!response.ok) throw new Error('Discount update failed');
      const data = await response.json();
      if (!Array.isArray(data.discount_codes)) throw new Error('Invalid discount response');
      CartDiscount.ITG_knownCodes.set(this.dataset.sectionId, data.discount_codes.filter((ITG_code) => ITG_code.applicable).map((ITG_code) => ITG_code.code));

      const ITG_applied = data.discount_codes.find((ITG_discount) => ITG_discount.code.toLowerCase() === discountCodeValue.toLowerCase());
      if (!ITG_applied?.applicable) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
        this.#ITG_status('This coupon is invalid or does not apply to this order.');
        deferredPromise.resolve({
          cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
        });
        return;
      }

      const newHtml = data.sections?.[this.dataset.sectionId];
      if (typeof newHtml !== 'string' || !newHtml.trim()) {
        deferredPromise.resolve({ cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data) });
        window.location.reload();
        return;
      }
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code.toLowerCase() === discountCodeValue.toLowerCase() && discount.applicable === true;
          })
        ) {
          this.#ITG_syncPills(data);
          this.#ITG_status('Coupon saved. Shipping discounts are checked at checkout after you enter your address.');
          discountCode.value = '';
          deferredPromise.resolve({
            cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
          });
          return;
        }
      }

      deferredPromise.resolve({
        cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
      });
      // Clear the input explicitly: data-skip-node-update on <input name="discount"> means
      // morphSection no longer syncs the input value from the server-rendered empty state,
      // so without this the user's typed code stays in the field after a successful apply.
      discountCode.value = '';
      await morphSection(this.dataset.sectionId, newHtml, { mode: this.closest('theme-drawer') ? 'hydration' : 'full' });
      this.#ITG_syncPills(data);
      this.#ITG_status('Coupon applied. Your order total has been updated.');
    } catch (error) {
      deferredPromise.reject(error);
      if (error instanceof Error && error.name !== 'AbortError') {
        this.#ITG_requestError();
        this.dispatchEvent(
          new CartErrorEvent({
            error: error.message || 'Failed to apply discount',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      }
    } finally {
      this.#activeFetch = null;
      this.#ITG_setBusy(false);
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (this.#activeFetch) return;

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof Element) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();
    this.#ITG_setBusy(true);
    this.#ITG_status('Removing coupon…');
    const deferredPromise = CartDiscountUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartDiscountUpdateEvent({
        discountCodes: existingDiscounts.map((code) => ({ code })),
        promise: deferredPromise.promise,
      })
    );

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(`${Theme.routes.cart_update_url.replace(/\.js$/, '')}.js`, {
        ...config,
        signal: abortController.signal,
      });

      if (!response.ok) throw new Error('Discount update failed');
      const data = await response.json();
      if (!Array.isArray(data.discount_codes)) throw new Error('Invalid discount response');
      CartDiscount.ITG_knownCodes.set(this.dataset.sectionId, data.discount_codes.filter((ITG_code) => ITG_code.applicable).map((ITG_code) => ITG_code.code));

      deferredPromise.resolve({
        cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
      });

      const ITG_html = data.sections?.[this.dataset.sectionId];
      if (typeof ITG_html !== 'string' || !ITG_html.trim()) {
        window.location.reload();
        return;
      }
      await morphSection(this.dataset.sectionId, ITG_html, {
        mode: this.closest('theme-drawer') ? 'hydration' : 'full',
      });
      this.#ITG_syncPills(data);
      this.#ITG_status('Coupon removed. Your order total has been updated.');
    } catch (error) {
      deferredPromise.reject(error);
      if (error instanceof Error && error.name !== 'AbortError') {
        this.#ITG_requestError();
        this.dispatchEvent(
          new CartErrorEvent({
            error: error.message || 'Failed to remove discount',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      }
    } finally {
      this.#activeFetch = null;
      this.#ITG_setBusy(false);
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');

    const errorMessage = type === 'discount_code' ? 'Invalid discount code' : 'Discount not applicable for shipping';
    this.dispatchEvent(
      new CartErrorEvent({
        error: errorMessage,
        code: 'VALIDATION_CUSTOM',
      })
    );
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [...(CartDiscount.ITG_knownCodes.get(this.dataset.sectionId) || [])];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return [...new Set(discountCodes)];
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
